import { Client } from '@stomp/stompjs';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { getAccessToken } from '@/auth/accessTokenStore';
import { refreshSession } from '@/auth/session';

/**
 * Keeps the work fresh: any message on the workspace's work topic refreshes it.
 *
 * The topic carries a bare "something changed", never the change, so the client asks again rather
 * than patching what it holds. A missed message costs at most the polling interval on the queries.
 *
 * @param tenantId the workspace; nothing connects until it is known
 * @param identity who is signed in, so a different person gets a connection of their own
 * @returns whether the topic is connected
 */
export function useWorkLive(tenantId: string | undefined, identity: string | null): boolean {
  const queries = useQueryClient();
  const [connectedTenant, setConnectedTenant] = useState<string | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    let stopped = false;
    const resync = () => {
      if (!stopped) {
        void queries.invalidateQueries({ queryKey: ['work'] });
      }
    };
    const url = new URL('/api/dispatch/live', window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const client = new Client({
      brokerURL: url.href,
      reconnectDelay: 1000,
      connectionTimeout: 8000,
      heartbeatIncoming: 0,
      heartbeatOutgoing: 10000,
      beforeConnect: async () => {
        if (!getAccessToken()) await refreshSession();
        client.connectHeaders = { Authorization: 'Bearer ' + (getAccessToken() ?? '') };
      },
      onConnect: () => {
        client.subscribe('/topic/work/' + tenantId, resync);
        setConnectedTenant(tenantId);
        resync();
      },
      onWebSocketClose: () => {
        if (!stopped) setConnectedTenant(null);
      },
      onStompError: () => {
        void refreshSession();
      },
    });
    client.activate();
    return () => {
      stopped = true;
      void client.deactivate();
    };
  }, [tenantId, identity, queries]);
  return tenantId !== undefined && connectedTenant === tenantId;
}
